/**
 * Mobile Login page for InDeep authentication.
 * 
 * Implements Figma design node 5:165 - mobile login screen.
 * Implements Figma design node 36:59 - mobile OTP verification screen.
 * Uses email OTP verification flow consistent with desktop login.
 * 
 * SECURITY: Authenticated users are redirected to home.
 * Email is pseudonymous identifier.
 * Non-enumerating: success message doesn't reveal account existence.
 * 
 * Mobile-first implementation:
 * - No fake device frames or status bars
 * - Uses 100dvh for proper mobile viewport handling
 * - 48px+ touch targets
 * - Proper inputmode and autocomplete attributes
 * - aria-live for accessible error announcements
 * 
 * Rules-of-Hooks compliance:
 * - All hooks declared at top level
 * - All useEffect hooks called before any early returns
 * - No hooks after conditional returns
 */
import { useState, FormEvent, useRef, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { beginOTP, verifyOTP, AuthError, isValidEmail, isSafeDisplayableError, FingerprintSignals } from '@/api/auth';
import { collectFingerprint } from '@/utils/fingerprint';
import { useAuthStore } from '@/store/authStore';
import { OTPInput, OTPInputHandle } from '@/components/OTPInput';
import wordmarkSvg from '@/assets/wordmark.svg';
import './MobileLoginPage.css';

type FormState = 'idle' | 'submitting' | 'success' | 'error';
type Step = 'email' | 'otp';

// Resend countdown duration in seconds (45 seconds per Figma)
const RESEND_COUNTDOWN_SECONDS = 45;

export function MobileLoginPage() {
  // ===== HOOKS - All hooks MUST be before any early returns =====
  
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<Step>('email');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [otpCode, setOtpCode] = useState('');
  
  // Resend countdown state
  const [resendCountdown, setResendCountdown] = useState(RESEND_COUNTDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState('');
  
  // OTP input ref for focus management
  const otpInputRef = useRef<OTPInputHandle>(null);
  
  // Ref-backed guard for duplicate submit protection (synchronous, not state-based)
  const verifyInFlightRef = useRef(false);

  // Get auth status from store
  const authStatus = useAuthStore((state) => state.authStatus);

  // Internal submit handler - uses useCallback to capture email in deps
  const handleOtpSubmitWithCode = useCallback(async (code: string) => {
    // Synchronous ref guard
    if (verifyInFlightRef.current) return;
    verifyInFlightRef.current = true;
    
    try {
      setFormState('submitting');
      setErrorMessage('');

      let fingerprint: FingerprintSignals | undefined;
      try {
        fingerprint = await collectFingerprint();
      } catch {
        // Fingerprint collection failed - proceed without fingerprint
      }

      await verifyOTP(email, code, fingerprint);
      window.location.href = '/';
    } catch (err) {
      setFormState('error');
      if (err instanceof AuthError) {
        const statusMsg = err.status ? ` (HTTP ${err.status})` : '';
        if (isSafeDisplayableError(err.message)) {
          setErrorMessage(err.message);
        } else {
          setErrorMessage('Verification failed' + statusMsg + '. Please try again.');
        }
      } else if (err instanceof Error) {
        setErrorMessage('Verification failed. Please try again later.');
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
      // Clear OTP input on error so user can retry
      setOtpCode('');
      otpInputRef.current?.clear();
    } finally {
      verifyInFlightRef.current = false;
    }
  }, [email]);

  // Handle resend OTP
  const handleResend = useCallback(async () => {
    if (!isValidEmail(email) || isResending) return;

    setIsResending(true);
    setResendError('');

    let fingerprint: FingerprintSignals | undefined;
    try {
      fingerprint = await collectFingerprint();
    } catch {
      // Fingerprint collection failed - proceed without fingerprint
    }

    try {
      // ACT-INDEEP-AUTH-UNIFIED-ENTRY01: use the unified beginOTP seam for
      // resend so we never issue a wrong-purpose OTP. The server resolves
      // account state and issues a verification-OTP (for pending accounts)
      // or a login-OTP (for active accounts) as appropriate.
      await beginOTP(email, fingerprint);
      // Reset countdown after successful resend
      setResendCountdown(RESEND_COUNTDOWN_SECONDS);
    } catch {
      setResendError("We couldn't send a new code. Please try again later.");
    } finally {
      setIsResending(false);
    }
  }, [email, isResending]);

  // Handle email form submit
  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Client-side validation: show inline error for empty field
    if (!email.trim()) {
      setFormState('error');
      setErrorMessage('Enter your email address.');
      return;
    }

    if (!isValidEmail(email)) {
      setFormState('error');
      setErrorMessage('Enter a valid email address.');
      return;
    }

    setFormState('submitting');
    setErrorMessage('');

    let fingerprint: FingerprintSignals | undefined;
    try {
      fingerprint = await collectFingerprint();
    } catch {
      // Fingerprint collection failed - proceed without fingerprint
    }

    try {
      // ACT-INDEEP-AUTH-UNIFIED-ENTRY01: use the unified beginOTP seam. The
      // server resolves account state internally and decides between creating
      // a new pending unclassified account (unknown email) and issuing a
      // login OTP (existing active account) — the public surface is one
      // operation, so the UI does NOT need to choose.
      await beginOTP(email, fingerprint);
      setFormState('success');
      setStep('otp');
    } catch (err) {
      setFormState('error');
      if (err instanceof AuthError) {
        const statusMsg = err.status ? ` (HTTP ${err.status})` : '';
        if (isSafeDisplayableError(err.message)) {
          setErrorMessage(err.message);
        } else {
          setErrorMessage('Sign-in unavailable' + statusMsg + '. Please try again later.');
        }
      } else if (err instanceof Error) {
        setErrorMessage('Sign-in failed. Please try again later.');
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    }
  };

  // Handle OTP code change - tracks code for UI state
  const handleOtpChange = (code: string) => {
    setOtpCode(code);
    // Clear error when user starts typing
    if (formState === 'error' && errorMessage) {
      setFormState('idle');
      setErrorMessage('');
    }
  };

  // Handle OTP submit from button click (uses current state)
  const handleOtpSubmit = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    // Synchronous ref guard
    if (verifyInFlightRef.current) return;
    
    if (!otpCode.trim() || otpCode.length < 4) {
      setFormState('error');
      setErrorMessage('Enter the complete verification code.');
      return;
    }

    await handleOtpSubmitWithCode(otpCode);
  };

  const handleReset = () => {
    setEmail('');
    setOtpCode('');
    setStep('email');
    setFormState('idle');
    setErrorMessage('');
    setResendCountdown(RESEND_COUNTDOWN_SECONDS);
    setResendError('');
    verifyInFlightRef.current = false;
  };

  // Format countdown time as M:SS
  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ===== USEEFFECTS - Must be before any early returns =====
  
  // Start resend countdown when OTP step is shown and clear OTP state
  useEffect(() => {
    if (step === 'otp') {
      // Clear OTP state when entering OTP step
      setOtpCode('');
      setFormState('idle');
      setErrorMessage('');
      setResendError('');
      otpInputRef.current?.clear();
      setResendCountdown(RESEND_COUNTDOWN_SECONDS);
      // Reset in-flight ref on step transition
      verifyInFlightRef.current = false;
    }
  }, [step]);

  // Resend countdown timer
  useEffect(() => {
    if (step !== 'otp' || resendCountdown <= 0) return;

    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [step, resendCountdown]);

  // ===== EARLY RETURNS - After all hooks and useEffects =====
  
  // SECURITY: Redirect authenticated users to home
  if (authStatus === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  // Generate unique IDs for accessibility
  const emailErrorId = 'email-error';

  return (
    <div className="auth-page">
      {step === 'otp' ? (
        /* OTP Verification Step - Full replacement screen */
        /* Figma design node 36:59 */
        <div className="otp-screen">
          <div className="otp-screen-inner">
            <img 
              src={wordmarkSvg} 
              alt="InDeep" 
              className="otp-logo"
            />
            
            {/* Wrapped in form for semantic HTML and Enter key submission */}
            <form className="otp-form" onSubmit={handleOtpSubmit} noValidate>
              <div className="otp-header">
                <h1 className="otp-title">Check your email</h1>
                <p className="otp-description">
                  We've sent a verification code to your email.
                </p>
                <p className="otp-email">{email}</p>
              </div>

              {/* OTP Input - 4 separate boxes */}
              <OTPInput
                ref={otpInputRef}
                length={4}
                disabled={formState === 'submitting'}
                onChange={handleOtpChange}
                error={formState === 'error'}
                autoFocus
              />

              {/* Error message */}
              {formState === 'error' && errorMessage && (
                <div 
                  className="otp-field-error field-error" 
                  role="alert"
                  aria-live="polite"
                >
                  {errorMessage}
                </div>
              )}

              {/* Resend section */}
              <div className="otp-resend">
                {resendCountdown > 0 ? (
                  <span>Resend code in <span className="otp-resend-countdown">{formatCountdown(resendCountdown)}</span></span>
                ) : (
                  <>
                    {resendError ? (
                      <span className="otp-resend-error">{resendError}</span>
                    ) : (
                      <button
                        type="button"
                        className="otp-resend-button"
                        onClick={handleResend}
                        disabled={isResending}
                      >
                        {isResending ? 'Sending...' : 'Resend code'}
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Actions - pinned to bottom */}
              <div className="otp-actions">
                <button
                  type="submit"
                  className="otp-verify-button"
                  disabled={formState === 'submitting' || otpCode.length < 4}
                >
                  {formState === 'submitting' ? 'Verifying...' : 'Verify'}
                </button>

                {/* Back button with chevron icon */}
                <button
                  type="button"
                  className="otp-back"
                  onClick={handleReset}
                >
                  <svg 
                    className="otp-back-icon"
                    viewBox="0 0 14 14" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path 
                      d="M9 1L3 7L9 13" 
                      stroke="currentColor" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                  Back
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        /* Email Step - Full replacement screen */
        <div className="auth-shell">
          {/* Wordmark */}
          <img 
            src={wordmarkSvg} 
            alt="InDeep" 
            className="auth-logo"
          />

          {/* Heading */}
          <h1 className="auth-title">Sign in</h1>

          {/* Description */}
          <p className="auth-subtitle">
            Enter your email. We'll send a code — no password needed.
          </p>

          {/* Email Form */}
          <form onSubmit={handleEmailSubmit} className="auth-form" noValidate>
            <div className="field">
              <label htmlFor="auth-email">Email</label>
              <input
                id="auth-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // Clear error when user starts typing
                  if (formState === 'error' && errorMessage) {
                    setFormState('idle');
                    setErrorMessage('');
                  }
                }}
                placeholder="you@example.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                spellCheck="false"
                disabled={formState === 'submitting'}
                aria-describedby={formState === 'error' ? emailErrorId : undefined}
                required
              />
              {formState === 'error' && errorMessage && (
                <div 
                  id={emailErrorId} 
                  className="field-error" 
                  role="alert"
                  aria-live="polite"
                >
                  {errorMessage}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="primary-button"
              disabled={formState === 'submitting'}
            >
              {formState === 'submitting' ? 'Sending...' : 'Continue'}
            </button>
          </form>

          {/* Footer */}
          <div className="auth-footer">
            <p className="auth-footer-text">
              By continuing you agree to our{' '}
              <a href="/terms" className="auth-link">Terms</a>{' '}
              and{' '}
              <a href="/privacy" className="auth-link">Privacy Policy</a>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
